import {
  type IdentifierRecord,
  type ScanAnalyticsDailyDocument,
  type ScanAnalyticsWorkspaceDailyDocument,
} from '@myplayer/schema';
import { IconAlertCircle, IconCheck, IconProducts, IconQrCode, IconRecall } from '@myplayer/ui';
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { canUseAllWorkspacesScope, useAuth } from './auth-context.js';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardIcon,
  CardTitle,
  ErrorBanner,
  Skeleton,
  Tag,
  useContextMenu,
} from './components/ui/primitives.js';
import { buildOverviewProductContextMenuItems } from './context-menu-items.js';
import {
  callGetWorkspaceScanAnalyticsFunction,
  type GetWorkspaceScanAnalyticsCallablePayload,
  type GetWorkspaceScanAnalyticsCallableResult,
} from './firebase.js';
import {
  aggregateOverviewScanAnalytics,
  buildDayWindowList,
  deriveNeedsAttentionItems,
  getTodayUtcIsoDay,
  isAdminCdnEnabled,
  mergeWorkspaceScanAnalyticsResults,
  type OverviewTimeWindow,
} from './overview-analytics-helpers.js';
import { OverviewAnalyticsSection } from './overview-charts.js';
import { formatPlainAdminError } from './strings.js';
import { useWorkspaceIdentifiers } from './use-workspace-identifiers.js';

export interface OverviewPageProps {
  readonly initialIdentifiers?: readonly IdentifierRecord[];
  readonly initialRollupResult?: GetWorkspaceScanAnalyticsCallableResult;
  readonly initialWorkspaceDailyRollups?: readonly ScanAnalyticsWorkspaceDailyDocument[];
  readonly initialDailyRecords?: readonly ScanAnalyticsDailyDocument[];
  readonly defaultWindowDays?: OverviewTimeWindow;
  readonly referenceDay?: string;
  readonly onFetchAnalytics?: (
    payload: GetWorkspaceScanAnalyticsCallablePayload,
  ) => Promise<GetWorkspaceScanAnalyticsCallableResult>;
}

export function OverviewPage({
  initialIdentifiers,
  initialRollupResult,
  initialWorkspaceDailyRollups,
  initialDailyRecords,
  defaultWindowDays = 7,
  referenceDay,
  onFetchAnalytics,
}: OverviewPageProps = {}): ReactElement {
  const { session, availableWorkspaces, workspaceScope, switchWorkspace } = useAuth();
  const navigate = useNavigate();
  const { bindContextMenu, contextMenuElement } = useContextMenu();

  const canViewAllWorkspaces = canUseAllWorkspacesScope(session, availableWorkspaces);
  const isAllWorkspacesMode = workspaceScope === 'ALL' && canViewAllWorkspaces;

  const targetWorkspaceIds = useMemo<readonly string[]>(() => {
    if (!session) return [];
    if (isAllWorkspacesMode && availableWorkspaces.length > 0) {
      return availableWorkspaces.map((ws) => ws.workspaceId);
    }
    return [session.activeWorkspaceId];
  }, [session, isAllWorkspacesMode, availableWorkspaces]);

  const workspaceNameMap = useMemo<ReadonlyMap<string, string>>(() => {
    const map = new Map<string, string>();
    for (const ws of availableWorkspaces) {
      map.set(ws.workspaceId, ws.name);
    }
    if (session && !map.has(session.activeWorkspaceId)) {
      map.set(session.activeWorkspaceId, session.activeWorkspaceId);
    }
    return map;
  }, [availableWorkspaces, session]);

  const {
    records: rawIdentifiers,
    loading: identifiersLoading,
    listenerError: identifiersError,
  } = useWorkspaceIdentifiers(session, initialIdentifiers, undefined, {
    workspaceIds: targetWorkspaceIds,
  });

  const identifiers = useMemo<readonly IdentifierRecord[]>(() => {
    if (isAllWorkspacesMode || !session) return rawIdentifiers;
    return rawIdentifiers.filter(
      (rec) => !rec.workspaceId || rec.workspaceId === session.activeWorkspaceId,
    );
  }, [rawIdentifiers, isAllWorkspacesMode, session]);

  const [windowDays, setWindowDays] = useState<OverviewTimeWindow>(defaultWindowDays);
  const effectiveReferenceDay = useMemo(() => referenceDay ?? getTodayUtcIsoDay(), [referenceDay]);
  const currentWindowDays = useMemo(
    () => buildDayWindowList(windowDays, effectiveReferenceDay),
    [windowDays, effectiveReferenceDay],
  );
  const windowFromDay = currentWindowDays[0] ?? effectiveReferenceDay;
  const windowToDay = currentWindowDays[currentWindowDays.length - 1] ?? effectiveReferenceDay;

  const hasInitialAnalyticsSeed =
    initialRollupResult !== undefined ||
    initialWorkspaceDailyRollups !== undefined ||
    initialDailyRecords !== undefined;

  const [rollupResult, setRollupResult] = useState<GetWorkspaceScanAnalyticsCallableResult | null>(
    initialRollupResult ?? null,
  );
  const [analyticsLoading, setAnalyticsLoading] = useState<boolean>(
    !hasInitialAnalyticsSeed && session !== null,
  );
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);

  const loadAnalytics = useCallback(async (): Promise<void> => {
    if (hasInitialAnalyticsSeed) {
      setRollupResult(initialRollupResult ?? null);
      setAnalyticsLoading(false);
      return;
    }
    if (!session) {
      setAnalyticsLoading(false);
      return;
    }
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    try {
      const fetchFn = onFetchAnalytics ?? callGetWorkspaceScanAnalyticsFunction;
      const wsIdsToFetch =
        targetWorkspaceIds.length > 0 ? targetWorkspaceIds : [session.activeWorkspaceId];
      if (wsIdsToFetch.length === 1) {
        const singleWsId = wsIdsToFetch[0] ?? session.activeWorkspaceId;
        const res = await fetchFn({
          orgId: session.orgId,
          workspaceId: singleWsId,
          fromDay: windowFromDay,
          toDay: windowToDay,
        });
        setRollupResult(res);
      } else {
        const results = await Promise.all(
          wsIdsToFetch.map((wsId) =>
            fetchFn({
              orgId: session.orgId,
              workspaceId: wsId,
              fromDay: windowFromDay,
              toDay: windowToDay,
            }),
          ),
        );
        setRollupResult(mergeWorkspaceScanAnalyticsResults(results));
      }
    } catch (err) {
      setAnalyticsError(
        formatPlainAdminError(err, 'Unable to load scan analytics for this workspace.'),
      );
    } finally {
      setAnalyticsLoading(false);
    }
  }, [
    hasInitialAnalyticsSeed,
    initialRollupResult,
    onFetchAnalytics,
    session,
    targetWorkspaceIds,
    windowFromDay,
    windowToDay,
  ]);

  useEffect(() => {
    void loadAnalytics();
  }, [loadAnalytics]);

  const filteredWorkspaceDailyRollups = useMemo(() => {
    if (initialWorkspaceDailyRollups === undefined) return undefined;
    if (isAllWorkspacesMode || !session) return initialWorkspaceDailyRollups;
    return initialWorkspaceDailyRollups.filter(
      (rec) => !rec.workspaceId || rec.workspaceId === session.activeWorkspaceId,
    );
  }, [initialWorkspaceDailyRollups, isAllWorkspacesMode, session]);

  const filteredDailyRecords = useMemo(() => {
    if (initialDailyRecords === undefined) return undefined;
    if (isAllWorkspacesMode || !session) return initialDailyRecords;
    return initialDailyRecords.filter(
      (rec) => !rec.workspaceId || rec.workspaceId === session.activeWorkspaceId,
    );
  }, [initialDailyRecords, isAllWorkspacesMode, session]);

  const analytics = useMemo(
    () =>
      aggregateOverviewScanAnalytics({
        ...(rollupResult !== null ? { rollupResult } : {}),
        ...(filteredWorkspaceDailyRollups !== undefined
          ? { workspaceDailyRollups: filteredWorkspaceDailyRollups }
          : {}),
        ...(filteredDailyRecords !== undefined ? { dailyRecords: filteredDailyRecords } : {}),
        identifiers,
        workspaceNameMap,
        windowDays,
        referenceDay: effectiveReferenceDay,
      }),
    [
      rollupResult,
      filteredWorkspaceDailyRollups,
      filteredDailyRecords,
      identifiers,
      workspaceNameMap,
      windowDays,
      effectiveReferenceDay,
    ],
  );

  const attention = useMemo(
    () => deriveNeedsAttentionItems(identifiers, workspaceNameMap),
    [identifiers, workspaceNameMap],
  );

  const handleOpenProductInWorkspace = (
    identifierKey: string,
    targetWorkspaceId?: string,
  ): void => {
    if (targetWorkspaceId && session && targetWorkspaceId !== session.activeWorkspaceId) {
      switchWorkspace(targetWorkspaceId);
    }
    const wsQuery = targetWorkspaceId
      ? `&workspaceId=${encodeURIComponent(targetWorkspaceId)}`
      : '';
    navigate(`/products?select=${encodeURIComponent(identifierKey)}${wsQuery}`);
  };

  return (
    <div className="space-y-4" data-testid="overview-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-page-title">Overview</h1>
      </div>

      {identifiersError !== null && (
        <ErrorBanner
          title="Could not load product list"
          message={formatPlainAdminError(identifiersError, 'Product records could not be loaded.')}
          nextStep="Refresh the page or verify your workspace access."
        />
      )}

      {/* 4 KPI Summary Cards */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        {[
          {
            testId: 'kpi-total-products',
            icon: <IconProducts />,
            label: 'Total products',
            value: identifiersLoading ? '—' : attention.totalProducts,
            sub: `${attention.publishedCount} published · ${attention.unpublishedCount} unpublished`,
          },
          {
            testId: 'kpi-total-scans',
            icon: <IconQrCode />,
            label: `Scans (${windowDays} days)`,
            value: analyticsLoading ? '—' : analytics.totalScans.toLocaleString(),
            sub: isAdminCdnEnabled()
              ? `Avg ${analytics.averageDailyScans.toLocaleString()}/day · ${analytics.cacheHitRatePct}% fast-edge hit`
              : `Avg ${analytics.averageDailyScans.toLocaleString()}/day`,
          },
          {
            testId: 'kpi-needs-attention',
            icon: <IconAlertCircle />,
            label: 'Needs attention',
            value: identifiersLoading ? '—' : attention.items.length,
            sub: `${attention.brokenLinkCount} broken links · ${attention.unpublishedCount} unpublished`,
          },
          {
            testId: 'kpi-active-recalls',
            icon: <IconRecall />,
            label: 'Active recalls',
            value: identifiersLoading ? '—' : attention.recalledCount,
            sub: attention.recalledCount > 0 ? 'Recall active' : 'None',
          },
        ].map((kpi) => (
          <Card key={kpi.testId} data-testid={kpi.testId} className="h-full">
            <CardHeader className="pb-2">
              <div className="flex min-w-0 items-center gap-2.5">
                <CardIcon>{kpi.icon}</CardIcon>
                <span className="min-w-0 break-words text-[12px] font-medium leading-tight text-fg-secondary">
                  {kpi.label}
                </span>
              </div>
            </CardHeader>
            <CardContent className="space-y-1">
              <div className="text-[24px] font-bold leading-tight text-fg tabular-nums">
                {kpi.value}
              </div>
              <p className="text-[12px] text-fg-secondary tabular-nums">{kpi.sub}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Needs Attention Panel */}
      <Card data-testid="overview-needs-attention-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <CardIcon>
              <IconAlertCircle />
            </CardIcon>
            <CardTitle>Needs attention</CardTitle>
          </div>
          <Badge variant={attention.items.length > 0 ? 'warning' : 'valid'}>
            {attention.items.length === 0
              ? 'All healthy'
              : `${String(attention.items.length)} ${attention.items.length === 1 ? 'item' : 'items'}`}
          </Badge>
        </CardHeader>
        <CardContent>
          {identifiersLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : attention.items.length === 0 ? (
            <div
              data-testid="overview-all-healthy-banner"
              className="flex items-center gap-2.5 text-[13px] text-fg-secondary"
            >
              <span className="text-status-valid">
                <IconCheck />
              </span>
              <span>All products are healthy — no active recalls or broken links.</span>
            </div>
          ) : (
            <ul className="divide-y divide-subtle" data-testid="needs-attention-list">
              {attention.items.map((item) => (
                <li
                  key={`${item.workspaceId ?? 'ws'}_${item.identifierKey}`}
                  data-testid={`attention-item-${item.identifierKey}`}
                  onContextMenu={bindContextMenu(() =>
                    buildOverviewProductContextMenuItems({
                      identifierKey: item.identifierKey,
                      primaryKey: item.primaryKey,
                      title: item.title,
                      actionLabel: item.actionLabel,
                      onOpenProduct: () =>
                        handleOpenProductInWorkspace(item.identifierKey, item.workspaceId),
                    }),
                  )}
                  className="flex flex-wrap items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={item.badgeVariant}>{item.badgeLabel}</Badge>
                      <span className="font-semibold text-fg">{item.title}</span>
                      <span className="font-mono text-[12px] text-fg-muted">
                        GTIN {item.primaryKey}
                      </span>
                      {isAllWorkspacesMode && item.workspaceName && (
                        <Tag
                          variant="outline"
                          size="sm"
                          data-testid={`attention-item-ws-badge-${item.identifierKey}`}
                        >
                          {item.workspaceName}
                        </Tag>
                      )}
                    </div>
                    <p className="mt-0.5 truncate text-[12px] text-fg-secondary">{item.summary}</p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant={item.category === 'RECALLED' ? 'destructive' : 'secondary'}
                    onClick={() =>
                      handleOpenProductInWorkspace(item.identifierKey, item.workspaceId)
                    }
                  >
                    {item.actionLabel}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <OverviewAnalyticsSection
        analytics={analytics}
        windowDays={windowDays}
        onChangeWindowDays={setWindowDays}
        loading={analyticsLoading}
        error={analyticsError}
        onRetry={() => void loadAnalytics()}
        showWorkspaceBadges={isAllWorkspacesMode}
        onSelectTopProduct={handleOpenProductInWorkspace}
      />
      {contextMenuElement}
    </div>
  );
}
